import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { BtnLink } from "@/components/BtnLink";
import { ListingCard } from "@/components/ListingCard";
import { useT } from "@/context/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1554149082-75d460afced3?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400";

function Hero({ t }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 160]);
  const scale = useTransform(scrollY, [0, 700], [1, 1.12]);
  return (
    <section data-testid="hero" className="relative h-[100svh] min-h-[600px] w-full overflow-hidden bg-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="La Côte d’Azur — littoral méditerranéen, Baie des Anges" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1a2f]/85 via-[#0a1a2f]/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a2f]/40 via-transparent to-transparent" />
      </motion.div>

      <div className="relative h-full max-w-[1500px] mx-auto px-5 sm:px-8 flex flex-col justify-center pt-20 text-ivory">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="overline mb-7">{t.hero.overline}<span className="sr-only"> — Conciergerie premium digitale &amp; physique</span></motion.h1>
        <h2 className="font-serif text-[11vw] sm:text-6xl lg:text-7xl leading-[1.02] max-w-3xl">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.4 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}>{line}{" "}</motion.span>
            </span>
          ))}
        </h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }} className="mt-9 max-w-lg text-ivory/85 text-base sm:text-lg leading-loose">{t.hero.sub}</motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.2 }} className="mt-11 flex flex-col sm:flex-row gap-3">
          <Link to="/confier-mon-bien" data-testid="hero-cta-confier" className="inline-flex items-center justify-center px-8 py-4 overline bg-ivory text-[#08131f] font-semibold hover:bg-white transition-colors duration-300">{t.hero.cta1}</Link>
          <Link to="/collection" data-testid="hero-cta-collection" className="inline-flex items-center justify-center px-8 py-4 overline border border-ivory/70 text-ivory hover:bg-ivory hover:text-[#0f2033] transition-colors duration-300">{t.hero.cta2}</Link>
        </motion.div>
      </div>
    </section>
  );
}

function Intro({ t }) {
  const [tt1, tt2] = t.intro.title.split(", ");
  return (
    <section data-testid="intro" className="py-28 sm:py-40 max-w-[1500px] mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
        <Reveal>
          <p className="overline text-[#0f2033]/60 mb-7">{t.intro.overline}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0f2033]">{tt1},<br />{tt2}</h2>
        </Reveal>
        <Reveal delay={0.1} className="self-end">
          <p className="text-ink/75 leading-[1.9] text-base sm:text-lg max-w-xl">{t.intro.text}</p>
        </Reveal>
      </div>
      <div className="mt-14 sm:mt-20 border-t border-ink/15" />
      <div className="grid md:grid-cols-3 md:divide-x divide-ink/15">
        {t.intro.pillars.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.1} className="py-12 md:py-14 md:px-10 first:md:pl-0 last:md:pr-0 border-b md:border-b-0 border-ink/15">
            <span className="font-serif text-2xl text-[#0f2033]/30">{p.n}</span>
            <div className="w-6 h-px bg-gold/80 mt-5 mb-6" />
            <h3 className="font-serif text-3xl font-medium text-[#0f2033]">{p.t}</h3>
            <p className="text-ink/70 text-sm mt-4 leading-[1.8]">{p.d}</p>
          </Reveal>
        ))}
      </div>
      <div className="mt-4 sm:mt-8 border-t border-ink/15">
        <div className="grid grid-cols-2 md:grid-cols-4 md:divide-x divide-ink/15">
          {t.intro.proof.map((p, i) => (
            <Reveal key={i} delay={i * 0.08} className="py-10 md:py-12 md:px-8 first:md:pl-0 border-b md:border-b-0 border-ink/15">
              <p className="font-serif text-3xl sm:text-4xl text-[#0f2033] leading-none">{p.v}</p>
              <p className="text-xs text-ink/55 mt-3 leading-relaxed">{p.d}</p>
            </Reveal>
          ))}
        </div>
        <p className="text-xs text-ink/40 leading-relaxed pb-2">{t.intro.proofNote}</p>
      </div>
      <div className="mt-10 sm:mt-14 grid md:grid-cols-2 md:divide-x divide-ink/15 border-t border-ink/15">
        {t.intro.expertise.map((e, i) => (
          <Reveal key={i} delay={i * 0.1} className="py-12 md:py-14 md:px-12 first:md:pl-0 last:md:pr-0 border-b md:border-b-0 border-ink/15">
            <h3 className="overline text-[#0f2033]">{e.t}</h3>
            <p className="overline text-gold mt-4">{e.s}</p>
            <p className="text-ink/70 text-sm mt-5 leading-[1.85] max-w-md">{e.d}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Marquis({ t }) {
  return (
    <div data-testid="marquee" className="py-5 sm:py-6 border-y border-ink/12 bg-sand/30 overflow-hidden">
      <Marquee speed={30} gradient={false} autoFill pauseOnHover={false}>
        {t.marquee.map((w, i) => (
          <span key={i} className="flex items-center">
            <span className="font-serif italic text-3xl sm:text-5xl text-[#0f2033]/80 ml-8 sm:ml-12">{w}</span>
            <span className="text-[#c8b78f] not-italic text-xl sm:text-2xl mx-8 sm:mx-12">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Owners({ t }) {
  return (
    <section data-testid="owners-home" className="py-24 sm:py-32 bg-ink text-ivory">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <p className="overline text-gold mb-6">{t.owners.overline}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{t.owners.title[0]}<br />{t.owners.title[1]}</h2>
            <p className="mt-8 text-ivory/70 leading-relaxed max-w-md">{t.owners.text}</p>
            <div className="mt-10"><BtnLink to="/confier-mon-bien" variant="outline" className="border-ivory/70 text-ivory hover:bg-ivory hover:text-ink" data-testid="owners-cta">{t.owners.cta}</BtnLink></div>
          </Reveal>
        </div>
        <Reveal>
          <ul className="divide-y divide-ivory/10 border-y border-ivory/10">
            {t.owners.services.map((s, i) => (
              <li key={i} className="py-4 flex items-baseline gap-5 group">
                <span className="font-serif text-gold text-sm w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-ivory/85 group-hover:text-ivory transition-colors">{s}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

function Performance({ t }) {
  return (
    <section data-testid="performance" className="py-24 sm:py-32 max-w-[1500px] mx-auto px-5 sm:px-8">
      <Reveal className="max-w-3xl"><p className="overline text-ink/50 mb-6">{t.perf.overline}</p><h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{t.perf.title.map((line, i) => <span key={i} className="block">{line}{" "}</span>)}</h2><p className="mt-8 text-ink/70 leading-[1.85] max-w-2xl">{t.perf.text}</p><p className="mt-5 text-sm text-ink/55 leading-relaxed max-w-2xl">{t.perf.eventNote}</p></Reveal>
      <div className="grid sm:grid-cols-2 gap-px bg-ink/10 mt-12 border border-ink/10">
        {t.perf.items.map((p, i) => (
          <Reveal key={i} delay={(i % 2) * 0.1} className={`bg-ivory p-10 sm:p-14 ${t.perf.items.length % 2 === 1 && i === t.perf.items.length - 1 ? "sm:col-span-2" : ""}`}><h3 className="font-serif text-3xl">{p.t}</h3><p className="text-ink/80 mt-5 leading-[1.85]">{p.d}</p></Reveal>
        ))}
      </div>
    </section>
  );
}

function CollectionPreview({ listings, t }) {
  return (
    <section data-testid="collection-preview" className="py-24 sm:py-32 bg-sand/25">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <Reveal><p className="overline text-ink/50 mb-5">{t.collectionPreview.overline}</p><h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl max-w-xl leading-[1.02]">{t.collectionPreview.title.map((line, i) => <span key={i} className="block">{line}{" "}</span>)}</h2></Reveal>
          <Reveal delay={0.1}><Link to="/collection" data-testid="see-all-listings" className="overline inline-flex items-center gap-2 border-b border-ink pb-1 hover:text-gold hover:border-gold transition-colors">{t.collectionPreview.link} <ArrowRight size={16} /></Link></Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {listings.slice(0, 6).map((l, i) => (<Reveal key={l.slug} delay={(i % 3) * 0.08}><ListingCard listing={l} index={i} /></Reveal>))}
        </div>
      </div>
    </section>
  );
}

function Why({ t }) {
  return (
    <section data-testid="why" className="py-24 sm:py-32 max-w-[1500px] mx-auto px-5 sm:px-8">
      <Reveal className="max-w-3xl mb-16"><p className="overline text-ink/50 mb-6">{t.why.overline}</p><h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{t.why.title.map((line, i) => <span key={i} className="block">{line}{" "}</span>)}</h2></Reveal>
      <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
        {t.why.items.map((w, i) => (<Reveal key={i} delay={(i % 2) * 0.1} className="border-t border-ink/15 pt-7"><h3 className="font-serif text-3xl">{w.t}</h3><p className="text-ink/75 mt-4 leading-[1.8] max-w-md">{w.d}</p></Reveal>))}
      </div>
    </section>
  );
}

function Testimonials({ t }) {
  return (
    <section data-testid="testimonials" className="py-24 sm:py-32 bg-ink text-ivory">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
        <Reveal className="max-w-3xl">
          <p className="overline text-gold mb-6">{t.testimonials.overline}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{t.testimonials.title.map((line, i) => <span key={i} className="block">{line}{" "}</span>)}</h2>
          <p className="mt-8 text-ivory/70 leading-[1.85] max-w-2xl">{t.testimonials.text}</p>
          <div className="mt-10"><BtnLink to="/a-propos" variant="outline-light" data-testid="experience-cta">{t.testimonials.cta}</BtnLink></div>
        </Reveal>
      </div>
    </section>
  );
}

function Potential({ t }) {
  return (
    <section data-testid="potential" className="py-24 sm:py-32 max-w-[1500px] mx-auto px-5 sm:px-8">
      <Reveal className="max-w-3xl"><p className="overline text-ink/50 mb-6">{t.potential.overline}</p><h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{t.potential.title.map((line, i) => <span key={i} className="block">{line}{" "}</span>)}</h2><p className="mt-8 text-ink/70 leading-[1.85] max-w-2xl">{t.potential.text}</p></Reveal>
      <Reveal delay={0.05}><p className="overline text-ink/40 mt-14">{t.potential.tag}</p></Reveal>
      <div className="grid sm:grid-cols-3 gap-px bg-ink/10 mt-6 border border-ink/10">
        {t.potential.items.map((it, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08} className="bg-ivory p-8 sm:p-12">
            <p className="overline text-ink/50">{it.k}</p>
            <p className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-none mt-6">{it.v}</p>
            <p className="text-sm text-ink/55 mt-5 leading-relaxed">{it.d}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.08}><p className="text-sm text-ink/60 mt-8 leading-relaxed max-w-3xl">{t.potential.commission}</p></Reveal>
      <Reveal delay={0.1}><p className="text-xs text-ink/40 mt-6 leading-relaxed max-w-3xl">{t.potential.note}</p></Reveal>
    </section>
  );
}

export default function Home() {
  const t = useT();
  const [listings, setListings] = useState([]);
  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {});
  }, []);
  return (
    <>
      <Seo title="Conciergerie premium à Cannes" description="Conciergerie premium à Cannes et sur la Côte d’Azur. Accompagnement digital partout en France, optimisation Airbnb & Booking et expérience voyageurs." path="/" />
      <Hero t={t} />
      <Intro t={t} />
      <Marquis t={t} />
      <Owners t={t} />
      <Performance t={t} />
      <CollectionPreview listings={listings} t={t} />
      <Why t={t} />
      <Testimonials t={t} />
      <Potential t={t} />
      <section data-testid="final-cta" className="py-28 sm:py-40 max-w-[1500px] mx-auto px-5 sm:px-8 text-center">
        <Reveal><h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl mx-auto">{t.finalCta.title}</h2><div className="mt-10 flex justify-center"><BtnLink to="/confier-mon-bien" variant="solid" data-testid="final-cta-btn">{t.finalCta.btn}</BtnLink></div></Reveal>
      </section>
    </>
  );
}
